import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Group,
  Text,
  NumberInput,
  Stack,
  Alert,
  Card,
  Avatar,
  Badge,
  LoadingOverlay,
  Divider,
  Paper,
  ThemeIcon,
  Box,
  SimpleGrid,
  TextInput,
  Checkbox,
  Select,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconTrophy,
  IconBox,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconCurrencyRupee,
  IconCalendar,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function CreateProductModal({
  isOpen,
  onClose,
  project,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    unitPrice: "",
    shippingFee: "",
    taxAmount: "0",
    targetOrderQuantity: "",
    optionId: "",
    isBookingOpen: true,
    eventDateTime: "",
  });

  // --- LOGIC: INITIALIZATION ---
  useEffect(() => {
    if (project && isOpen) {
      setFormData({
        unitPrice: project.unitPrice?.toString() || "",
        shippingFee: project.shippingFee?.toString() || "",
        taxAmount: "0",
        targetOrderQuantity: project.targetQuantity?.toString() || "",
        optionId: "",
        isBookingOpen: true,
        eventDateTime: "",
      });
      fetchPreview();
      setError("");
    }
  }, [project, isOpen]);

  // --- LOGIC: FETCH PREVIEW ---
  const fetchPreview = async () => {
    if (!project) return;
    setPreviewLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/projects/${project.id}/product-preview`,
        { headers: getAuthHeaders() },
      );
      const data = await response.json();
      if (data.success) {
        setPreview(data.data);
        // Auto-fill winner option if available
        if (data.data.productPreview?.winnerOption?.id) {
          setFormData((prev) => ({
            ...prev,
            optionId: data.data.productPreview.winnerOption.id,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching preview:", error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // --- LOGIC: SUBMIT ---
  const handleSubmit = async () => {
    // Validation
    if (!formData.unitPrice || Number(formData.unitPrice) <= 0)
      return setError("Please enter a valid unit price");
    if (
      !formData.targetOrderQuantity ||
      Number(formData.targetOrderQuantity) <= 0
    )
      return setError("Please enter a valid target quantity");
    if (project.projectType === "NEW_PRODUCT" && !formData.optionId)
      return setError("Please select a winning design option");
    if (project.projectType === "EVENT" && !formData.eventDateTime)
      return setError("Please select event date and time");

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/projects/${project.id}/create-product`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            forceCreate: false,
            unitPrice: parseFloat(formData.unitPrice),
            shippingFee: parseFloat(formData.shippingFee || 0),
            taxAmount: parseFloat(formData.taxAmount || 0),
            targetOrderQuantity: parseInt(formData.targetOrderQuantity),
            optionId: formData.optionId || undefined,
            isBookingOpen: formData.isBookingOpen,
            eventDateTime: formData.eventDateTime || undefined,
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        onSuccess(data.data);
        onClose();
      } else {
        setError(data.error?.message || "Failed to create product");
      }
    } catch (err) {
      setError("Network error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!project) return null;

  const totalCost =
    (Number(formData.unitPrice) || 0) +
    (Number(formData.shippingFee) || 0) +
    (Number(formData.taxAmount) || 0);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Create Product
        </Text>
      }
      size="lg"
      radius="md"
      padding="xl"
    >
      <Stack gap="md" pos="relative">
        <LoadingOverlay
          visible={loading || previewLoading}
          overlayProps={{ radius: "sm", blur: 2 }}
        />

        {/* 1. WARNING ALERT */}
        <Alert
          variant="light"
          color="yellow"
          title="Important: Project Status Change"
          icon={<IconAlertTriangle size={20} />}
          radius="md"
        >
          <Stack gap={4}>
            <Text size="sm" c="yellow.9">
              This action marks the project as <b>SUCCESSFUL</b> and creates a
              live product.
            </Text>
            <SimpleGrid cols={1} spacing={2}>
              <Group gap="xs">
                <IconCheck size={14} />
                <Text size="xs">Status changes to SUCCESSFUL</Text>
              </Group>
              <Group gap="xs">
                <IconCheck size={14} />
                <Text size="xs">Voting/Funding phase ends</Text>
              </Group>
              <Group gap="xs">
                <IconCheck size={14} />
                <Text size="xs">Inventory item created for orders</Text>
              </Group>
            </SimpleGrid>
          </Stack>
        </Alert>

        {error && (
          <Alert color="red" icon={<IconX size={16} />}>
            {error}
          </Alert>
        )}

        {/* 2. PROJECT SUMMARY */}
        <Card withBorder radius="md" bg="gray.0" padding="md">
          <Text fw={600} size="sm" mb="xs">
            Project Summary
          </Text>
          <SimpleGrid cols={2} spacing="xs" mb="md">
            <Box>
              <Text size="xs" c="dimmed">
                Title
              </Text>
              <Text fw={600} size="sm" lineClamp={1}>
                {project.title}
              </Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Type
              </Text>
              <Badge variant="outline" size="sm" color="gray">
                {project.projectType?.replace("_", " ")}
              </Badge>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Status
              </Text>
              <Badge color="green" size="sm" variant="dot">
                ACTIVE
              </Badge>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">
                Creator
              </Text>
              <Text size="sm" lineClamp={1}>
                {project.creator?.email}
              </Text>
            </Box>
          </SimpleGrid>

          {/* Winning Option Card (If applicable) */}
          {preview?.productPreview?.winnerOption && (
            <Card radius="sm" p="sm" withBorder>
              <Group wrap="nowrap">
                <ThemeIcon size="lg" radius="md" color="yellow" variant="light">
                  <IconTrophy size={20} />
                </ThemeIcon>
                <Box style={{ flex: 1 }}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    Winning Design
                  </Text>
                  <Text size="sm" fw={700} c="dark">
                    {preview.productPreview.winnerOption.name}
                  </Text>
                  <Text size="xs" c="blue" fw={600}>
                    {preview.productPreview.winnerOption.voteCount} Votes
                  </Text>
                </Box>
                {preview.productPreview.winnerOption.imageUrl && (
                  <Avatar
                    src={preview.productPreview.winnerOption.imageUrl}
                    size="lg"
                    radius="sm"
                  />
                )}
              </Group>
            </Card>
          )}
        </Card>

        {/* 3. PRODUCT CONFIGURATION */}
        <Box>
          <Text size="sm" fw={600} mb="sm">
            Product Configuration
          </Text>
          <Stack gap="sm">
            <SimpleGrid cols={2}>
              <NumberInput
                label="Unit Price"
                leftSection={<IconCurrencyRupee size={14} />}
                placeholder="0.00"
                required
                min={0}
                value={formData.unitPrice}
                onChange={(v) => setFormData({ ...formData, unitPrice: v })}
              />
              <NumberInput
                label="Shipping Fee"
                leftSection={<IconCurrencyRupee size={14} />}
                placeholder="0 for free"
                min={0}
                value={formData.shippingFee}
                onChange={(v) => setFormData({ ...formData, shippingFee: v })}
              />
            </SimpleGrid>

            <SimpleGrid cols={2}>
              <NumberInput
                label="Tax Amount"
                leftSection={<IconCurrencyRupee size={14} />}
                placeholder="0.00"
                min={0}
                value={formData.taxAmount}
                onChange={(v) => setFormData({ ...formData, taxAmount: v })}
              />
              <NumberInput
                label="Total Quantity"
                placeholder="Stock available"
                required
                min={1}
                value={formData.targetOrderQuantity}
                onChange={(v) =>
                  setFormData({ ...formData, targetOrderQuantity: v })
                }
              />
            </SimpleGrid>

            {project.projectType === "EVENT" && (
              <TextInput
                type="datetime-local"
                label="Event Date & Time"
                required
                leftSection={<IconCalendar size={16} />}
                value={formData.eventDateTime}
                onChange={(e) =>
                  setFormData({ ...formData, eventDateTime: e.target.value })
                }
              />
            )}

            {/* Manual Option Override (If needed) */}
            {project.projectType === "NEW_PRODUCT" &&
              preview?.productPreview?.options && (
                <Select
                  label="Winning Design Override"
                  placeholder="Select design"
                  data={project.options?.map((opt) => ({
                    value: opt.id,
                    label: `${opt.optionName} (${opt.voteCount} votes)`,
                  }))}
                  value={formData.optionId}
                  onChange={(v) => setFormData({ ...formData, optionId: v })}
                  searchable
                />
              )}

            <Checkbox
              label="Enable Booking Immediately"
              description="Users can place orders/tickets without payment flow. (useful for on venue payments or manual order taking)"
              checked={formData.isBookingOpen}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  isBookingOpen: e.currentTarget.checked,
                })
              }
              mt="xs"
            />
          </Stack>
        </Box>

        {/* 4. TOTALS BAR */}
        <Paper
          p="sm"
          bg="blue.0"
          radius="md"
          withBorder
          style={{ borderColor: "var(--mantine-color-blue-2)" }}
        >
          <Group justify="space-between">
            <Text size="sm" c="blue.8" fw={600}>
              Total Cost to User:
            </Text>
            <Text size="lg" c="blue.9" fw={800}>
              ₹{totalCost.toLocaleString()}
            </Text>
          </Group>
        </Paper>

        {/* 5. ACTIONS */}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="blue"
            leftSection={<IconBox size={18} />}
            loading={loading}
          >
            Confirm & Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
